<template>
  <div>
    <b-tabs v-model="activeTab" content-class="mt-3" justified class="custom-tabs d-sm-block">
      <b-tab
        :title="`${getTitle(i)}`"
        v-for="i in totalTabs"
        :key="i"
        :disabled="getRecurrentListBySlice(i).length === 0"
      >
        <div class="tab-content">
          <div
            class="tab-content-item"
            v-for="(rc, index) in getRecurrentListBySlice(i)"
            :key="index"
            :ref="'slot-' + ((i - 1) * 8 + index)"
          >
            <TimeSlot
              :item="rc"
              :payment_status="multiPackage.payment_status"
              :order="(i - 1) * 8 + index + 1"
            />
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import { Vue, Component, Watch } from 'vue-property-decorator'
import { createNamespacedHelpers } from 'vuex'
import TimeSlot from './TimeSlot'

const BookingStore = createNamespacedHelpers('booking')
const CalendarStore = createNamespacedHelpers('calendar')

@Component({
  components: { TimeSlot },
  computed: {
    ...BookingStore.mapState(['multiPackage']),
    ...CalendarStore.mapState(['isFetchingCalendar']),
  },
  methods: {
    ...CalendarStore.mapActions(['updateMaidCalendarByMonth']),
  },
})
export default class PackageTab extends Vue {
  currentItemIndex = null
  changeForm = {}
  calendar = []
  maidTotalHours = []
  maidCalendarAllMonth = []
  maidCalendarAvailable = {}
  isFetchingData = false
  activeTab = 0

  get availableRecurrentList() {
    if (this.multiPackage && Array.isArray(this.multiPackage.recurrentList)) {
      const valid = this.multiPackage.recurrentList.filter(
        (item) => item && item.start_datetime && !isNaN(new Date(item.start_datetime))
      )
      const invalid = this.multiPackage.recurrentList.filter(
        (item) => !item || !item.start_datetime || isNaN(new Date(item.start_datetime))
      )

      const sorted = valid.sort((a, b) => {
        const aTime = new Date(a.start_datetime).getTime()
        const bTime = new Date(b.start_datetime).getTime()
        return aTime - bTime
      })

      return [...sorted, ...invalid]
    }
    return []
  }

  getRecurrentListBySlice(index) {
    const start = (index - 1) * 8
    const end = 7 + (index - 1) * 8
    return this.availableRecurrentList.slice(start, end + 1)
  }

  getTitle(index) {
    const total =
      this.multiPackage && Array.isArray(this.multiPackage.recurrentList)
        ? this.multiPackage.recurrentList.length
        : 0
    const start = (index - 1) * 8 + 1
    let end = index * 8
    if (end > total) end = total
    return `${start} - ${end}`
  }

  @Watch('multiPackage.recurrentList', { deep: true })
  onRecurrentListChanged(newVal, oldVal) {
    if (typeof window !== 'undefined' && window.__preventAutoScroll) {
      this.$nextTick(() => this.$forceUpdate())
      return
    }
    try {
      if (newVal == null || !Array.isArray(newVal)) return

      const tabsCount = Math.max(1, this.totalTabs)
      const lastTabIndex = Math.max(0, tabsCount - 1)

      if (!Array.isArray(oldVal) || oldVal.length === 0) {
        this.activeTab = lastTabIndex
        this.$nextTick(() => {
          const lastIdx = Math.max(0, this.availableRecurrentList.length - 1)
          if (lastIdx >= 0) this.scrollToGlobalIndex(lastIdx)
        })
        return
      }

      const dateKey = (item) => {
        if (!item || !item.start_datetime) return null
        const d = new Date(item.start_datetime)
        if (isNaN(d)) return null
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd}`
      }

      const getKey = (item) => {
        if (!item) return null
        if (item.id != null) return `id:${item.id}`
        if (item.uuid != null) return `uuid:${item.uuid}`
        if (item.start_datetime != null) return `dt:${item.start_datetime}`
        return null
      }

      const prevDates = (oldVal || []).map(dateKey)
      const currDates = (newVal || []).map(dateKey)
      const sameDates =
        prevDates.length === currDates.length && prevDates.every((v, i) => v === currDates[i])

      if (sameDates) return

      let foundIndexInAvailable = -1
      const minLen = Math.min(oldVal.length, newVal.length)
      for (let i = 0; i < minLen; i++) {
        const ov = oldVal[i]
        const nv = newVal[i]
        if (nv && nv.start_datetime && (!ov || !ov.start_datetime)) {
          const k = getKey(nv)
          if (k != null) {
            foundIndexInAvailable = this.availableRecurrentList.findIndex((x) => getKey(x) === k)
          } else {
            foundIndexInAvailable = this.availableRecurrentList.findIndex(
              (x) => x && x.start_datetime === nv.start_datetime
            )
          }
          if (foundIndexInAvailable !== -1) break
        }
      }

      if (foundIndexInAvailable === -1) {
        const oldMap = new Map()
        oldVal.forEach((it, idx) => {
          const k = getKey(it)
          oldMap.set(k != null ? k : `__old_idx_${idx}`, { it, idx })
        })

        for (let i = 0; i < newVal.length; i++) {
          const newItem = newVal[i]
          const k = getKey(newItem)

          if (k != null) {
            const oldEntry = oldMap.get(k)
            if (!oldEntry) {
              foundIndexInAvailable = this.availableRecurrentList.findIndex((x) => getKey(x) === k)
              if (foundIndexInAvailable !== -1) break
            } else if (newItem.start_datetime !== oldEntry.it.start_datetime) {
              foundIndexInAvailable = this.availableRecurrentList.findIndex((x) => getKey(x) === k)
              if (foundIndexInAvailable !== -1) break
            }
          } else {
            if (newItem && newItem.start_datetime) {
              const idx = this.availableRecurrentList.findIndex(
                (x) => x && x.start_datetime === newItem.start_datetime
              )
              if (idx !== -1) {
                foundIndexInAvailable = idx
                break
              }
            }
            const idxFull = this.availableRecurrentList.findIndex(
              (x) => JSON.stringify(x) === JSON.stringify(newItem)
            )
            if (idxFull !== -1) {
              foundIndexInAvailable = idxFull
              break
            }
          }
        }

        if (foundIndexInAvailable === -1) {
          for (let i = 0; i < Math.min(oldVal.length, newVal.length); i++) {
            if (
              (oldVal[i] && newVal[i] && oldVal[i].start_datetime !== newVal[i].start_datetime) ||
              JSON.stringify(oldVal[i]) !== JSON.stringify(newVal[i])
            ) {
              const maybeKey = getKey(newVal[i])
              if (maybeKey != null) {
                foundIndexInAvailable = this.availableRecurrentList.findIndex(
                  (x) => getKey(x) === maybeKey
                )
              } else {
                foundIndexInAvailable = this.availableRecurrentList.findIndex(
                  (x) => JSON.stringify(x) === JSON.stringify(newVal[i])
                )
              }
              if (foundIndexInAvailable !== -1) break
            }
          }
        }
      }

      if (foundIndexInAvailable >= 0) {
        const targetTab = Math.min(Math.max(0, Math.floor(foundIndexInAvailable / 8)), lastTabIndex)
        if (targetTab !== this.activeTab) {
          this.activeTab = targetTab
          this.$nextTick(() => {
            this.scrollToGlobalIndex(foundIndexInAvailable)
          })
        } else {
          this.$nextTick(() => {
            this.scrollToGlobalIndex(foundIndexInAvailable)
          })
        }
      } else {
        this.activeTab = Math.min(this.activeTab, lastTabIndex)
      }
    } catch (e) {
      this.activeTab = Math.min(
        this.activeTab,
        Math.max(0, Math.ceil(this.availableRecurrentList.length / 8) - 1)
      )
    }
    this.$forceUpdate()
  }

  get totalTabs() {
    return Math.ceil(this.availableRecurrentList.length / 8)
  }

  get recurrentList() {
    return this.availableRecurrentList
  }

  scrollToGlobalIndex(globalIndex) {
    try {
      const refName = 'slot-' + globalIndex
      const el = this.$refs[refName]
      const node = Array.isArray(el) ? el[0] : el
      if (!node) return
      const target = node.$el || node

      const findScrollableParent = (el) => {
        let p = el.parentElement
        while (p) {
          const style = window.getComputedStyle(p)
          const overflowY = style.overflowY
          const canScroll =
            p.scrollHeight > p.clientHeight &&
            (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
          if (canScroll) return p
          p = p.parentElement
        }
        return document.scrollingElement || document.documentElement
      }

      const container = findScrollableParent(target)

      const elRect = target.getBoundingClientRect()
      const containerRect =
        container === document.scrollingElement || container === document.documentElement
          ? { top: 0, left: 0, height: window.innerHeight }
          : container.getBoundingClientRect()

      const headerOffset = 80 
      const targetCenterOffset = Math.round(container.clientHeight / 2 - elRect.height / 2)

      if (container === document.scrollingElement || container === document.documentElement) {
        const top = window.pageYOffset + elRect.top - headerOffset - targetCenterOffset
        window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: 'smooth' })
      } else {
        const scrollTop =
          container.scrollTop + (elRect.top - containerRect.top) - targetCenterOffset
        container.scrollTo({ top: Math.max(0, Math.round(scrollTop)), behavior: 'smooth' })
      }
    } catch (err) {
      // fallback
      try {
        const refName = 'slot-' + globalIndex
        const el = this.$refs[refName]
        const node = Array.isArray(el) ? el[0] : el
        const target = node && (node.$el || node)
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      } catch (e) {
        // silent
      }
    }
  }
}
</script>